import React, { useState, useEffect } from 'react'
import { Button } from '../../../../atoms'
import { CalculatorInput } from '../elements/CalculatorInput'
import { CalculatorCard } from '../elements/CalculatorCard'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title)

const CLICKOUT_LINK =
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3Dpartner%26utm_medium%3Duswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D&sig=eb3aedc769d9195e515401b38ecc342a38399481ca6e5e46415d73c4d941afc5&brand=uswitch'

const MortgageCalculator = () => {
  const initialDeposit = 25000
  const initialBorrowing = 200000
  const initialMortgageTerm = 25
  const initialInterestRate = 6.5

  const [deposit, setDeposit] = useState(initialDeposit)
  const [borrowing, setBorrowing] = useState(initialBorrowing)
  const [mortgageTerm, setMortgageTerm] = useState(initialMortgageTerm)
  const [interestRate, setInterestRate] = useState(initialInterestRate)
  const [propertyBudget, setPropertyBudget] = useState(0)
  const [totalInterest, setTotalInterest] = useState(0)
  const [totalCost, setTotalCost] = useState(0)
  const [monthlyPayment, setMonthlyPayment] = useState('0')

  const estimatedMonthlyPaymentData = {
    labels: [''],
    datasets: [
      {
        label: `Deposit`,
        data: [deposit],
        backgroundColor: '#000000',
        hoverBackgroundColor: '#000000',
      },
      {
        label: `Borrowing`,
        data: [borrowing],
        backgroundColor: '#AC96DE',
        hoverBackgroundColor: '#AC96DE',
      },
    ],
  }

  const estimatedMonthlyPaymentOptions = {
    indexAxis: 'y' as const,
    scales: {
      x: {
        stacked: true,
        display: false,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    drawOnChartArea: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  }

  const calculateTotalInterest = (
    deposit: number,
    borrowingAmount: number,
    interestRate: number,
    mortgageTerm: number,
  ) => {
    const interestRateDecimal = interestRate / 100
    const mortgageTermMonths = mortgageTerm * 12
    const monthlyInterestRate = interestRateDecimal / 12

    const numerator =
      borrowingAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, mortgageTermMonths)
    const denominator = Math.pow(1 + monthlyInterestRate, mortgageTermMonths) - 1
    const mortgagePayment = numerator / denominator

    const totalRepayment = mortgagePayment * mortgageTermMonths
    const totalInterest = totalRepayment - borrowing
    const monthlyPayment = totalRepayment / mortgageTermMonths
    const totalCosts = Math.round(totalRepayment) + Number(deposit)

    return {
      totalCost: Math.round(totalCosts),
      totalInterest: Math.round(totalInterest),
      monthlyPayment: Math.round(monthlyPayment)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    }
  }

  useEffect(() => {
    const { totalCost, totalInterest, monthlyPayment } = calculateTotalInterest(
      deposit,
      borrowing,
      interestRate,
      mortgageTerm,
    )

    setTotalCost(totalCost)
    setTotalInterest(totalInterest)
    setMonthlyPayment(monthlyPayment)
    setPropertyBudget(deposit + borrowing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const { totalCost, totalInterest, monthlyPayment } = calculateTotalInterest(
      deposit,
      borrowing,
      interestRate,
      mortgageTerm,
    )

    setTotalCost(totalCost)
    setTotalInterest(totalInterest)
    setMonthlyPayment(monthlyPayment)
    setPropertyBudget(deposit + borrowing)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit, borrowing, propertyBudget, mortgageTerm, interestRate])

  return (
    <div className='MortgageCalculator__wrapper'>
      <div className='MortgageCalculator'>
        <div className='MortgageCalculator__gauge-card MortgageCalculator__main-content-container'>
          <div className='MortgageCalculator__content-container'>
            <div className='MortgageCalculator__left-side'>
              <h1 className='MortgageCalculator__heading'>Mortgage Calculator</h1>
              <p className='MortgageCalculator__paragraph'>
                We&apos;ve got the ultimate mortgage calculator to arm yourself with the knowledge
                to approach your mortgage with confidence.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className='MortgageCalculator__panel'>
        <div className='MortgageCalculator__panel-inputs'>
          <label htmlFor={'calculator_deposit'} className='MortgageCalculator__panel-label'>
            <span className='MortgageCalculator__panel-label-title'>Your deposit</span>
            <span className='MortgageCalculator__panel-label-subtitle'>
              What you pay upfront towards your home.
            </span>
            <CalculatorInput
              id='calculator_deposit'
              name='deposit'
              type='text'
              format='thousands'
              resetButton={true}
              prefix={'£'}
              showDot={true}
              max={400000}
              min={5000}
              dotColor='#000000'
              value={25000}
              step={50}
              onChange={(e: any) => setDeposit(e.target.value)}
              enableRange={true}
            />
          </label>
          <label htmlFor={'calculator_borrowing'} className='MortgageCalculator__panel-label'>
            <span className='MortgageCalculator__panel-label-title'>
              How much do you need to borrow?
            </span>
            <span className='MortgageCalculator__panel-label-subtitle'>
              Usually you can borrow a maximum of around 4.5x your income
            </span>
            <CalculatorInput
              id='calculator_borrowing'
              name='borrowing'
              type='text'
              prefix={'£'}
              showDot={true}
              dotColor='#AC96DE'
              resetButton={true}
              max={500000}
              min={40000}
              format='thousands'
              step={1000}
              value={200000}
              onChange={(e: any) => setBorrowing(e.target.value)}
              enableRange={true}
            />
          </label>
          <label htmlFor={'property_budget'} className='MortgageCalculator__panel-label'>
            <span className='MortgageCalculator__panel-label-title'>Property value</span>
            <span className='MortgageCalculator__panel-label-subtitle'>
              Based on your deposit/balance and borrowing
            </span>
            <CalculatorInput
              id='property_budget'
              name='propertyBudget'
              type='text'
              prefix={'£'}
              resetButton={false}
              format='thousands'
              readOnly={true}
              value={propertyBudget}
              onChange={(e: any) => setPropertyBudget(e.target.value)}
            />
          </label>
          <label htmlFor={'mortgage_term'} className='MortgageCalculator__panel-label'>
            <span className='MortgageCalculator__panel-label-title'>Mortgage term</span>
            <span className='MortgageCalculator__panel-label-subtitle'>
              How long you plan to pay back your mortgage.
            </span>
            <CalculatorInput
              id='mortgage_term'
              name='mortgage_term'
              type='years'
              suffix='years'
              resetButton={true}
              max={40}
              value={25}
              min={20}
              step={1}
              enableRange={true}
              onChange={(e: any) => setMortgageTerm(e.target.value)}
            />
          </label>
          <label htmlFor={'interest_rate'} className='MortgageCalculator__panel-label'>
            <span className='MortgageCalculator__panel-label-title'>Interest rate</span>
            <span className='MortgageCalculator__panel-label-subtitle'>
              How much the lender charges you to borrow.
            </span>
            <CalculatorInput
              id='interest_rate'
              name='interest_rate'
              type='percent'
              value={6.5}
              min={1}
              max={10}
              suffix={'%'}
              resetButton={true}
              step={0.01}
              enableRange={true}
              onChange={(e: any) => setInterestRate(e.target.value)}
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor={'monthly_payment'} className='MortgageCalculator__panel-label'>
            <div className='MortgageCalculator__panel-monthly-payment-container'>
              <div className='MortgageCalculator__panel-monthly-payment-label'>
                <span className='MortgageCalculator__panel-label-title'>Monthly payment</span>
                <span className='MortgageCalculator__panel-label-subtitle'>
                  This is an estimation
                </span>
              </div>
              <div className='MortgageCalculator__panel-label-monthly-payment'>
                £ {monthlyPayment}
              </div>
            </div>
            <div className='MortgageCalculator__budget'>
              <Bar
                height={112}
                data={estimatedMonthlyPaymentData}
                options={estimatedMonthlyPaymentOptions}
              />
            </div>
          </label>
        </div>
        <div className='MortgageCalculator__panel-bottom'>
          <div className='MortgageCalculator__panel-cards'>
            <div className='MortgageCalculator__panel-card'>
              <CalculatorCard label='Monthly payment' value={`£ ${monthlyPayment}`} />
            </div>
            <div className='MortgageCalculator__panel-card'>
              <CalculatorCard label='Number of payments' value={`${mortgageTerm * 12}`} />
            </div>
            <div className='MortgageCalculator__panel-card'>
              <CalculatorCard
                label={`Total interest over ${mortgageTerm} years`}
                value={`£ ${totalInterest.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}
              />
            </div>
            <div className='MortgageCalculator__panel-card'>
              <CalculatorCard
                label={`Total cost over ${mortgageTerm} years`}
                value={`£ ${totalCost.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}
              />
            </div>
          </div>
          <div className='MortgageCalculator__panel-step'>
            <span className='MortgageCalculator__panel-step-title'>Next Steps</span>
            <span className='MortgageCalculator__panel-step-text'>
              These are indicative numbers to give you an estimation. Our mortgage partner Mojo can
              help you to get more accurate understanding of your borrowing.
            </span>
            <Button withIcon variant='primary' href={CLICKOUT_LINK}>
              Check out your mortgage options
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MortgageCalculator
